/**
 * Publish/Subscribe system for cross-component communication
 * Based on Shopify's theme architecture
 */

// PubSub Events Constants
window.PUB_SUB_EVENTS = {
  cartUpdate: 'cart:update',
  cartError: 'cart:error',
  cartItemAdd: 'cart:item-add',
  cartItemRemove: 'cart:item-remove',
  cartItemUpdate: 'cart:item-update',
  variantChange: 'product:variant-change',
  productFormSubmit: 'product:form-submit',
  sectionLoad: 'section:load',
  sectionUpdate: 'section:update',
  modalOpen: 'modal:open',
  modalClose: 'modal:close',
  reviewSubmit: 'review:submit',
  reviewUpdate: 'review:update'
};

class PubSub {
  constructor() {
    this.subscribers = {};
  }

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   * @param {object} options - Options object
   * @returns {function} Unsubscribe function
   */
  subscribe(event, callback, options = {}) {
    if (!this.subscribers[event]) {
      this.subscribers[event] = [];
    }

    const subscriber = {
      callback,
      once: options.once || false,
      id: Math.random().toString(36).substr(2, 9)
    };

    this.subscribers[event].push(subscriber);

    // Return unsubscribe function
    return () => {
      this.unsubscribe(event, subscriber.id);
    };
  }

  /**
   * Subscribe to an event that fires only once
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   * @returns {function} Unsubscribe function
   */
  subscribeOnce(event, callback) {
    return this.subscribe(event, callback, { once: true });
  }

  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {string} id - Subscriber ID
   */
  unsubscribe(event, id) {
    if (!this.subscribers[event]) return;

    this.subscribers[event] = this.subscribers[event].filter(
      subscriber => subscriber.id !== id
    );

    if (this.subscribers[event].length === 0) {
      delete this.subscribers[event];
    }
  }

  /**
   * Publish an event to all subscribers
   * @param {string} event - Event name
   * @param {*} data - Data to pass to subscribers
   * @param {object} options - Options object
   */
  publish(event, data = null, options = {}) {
    if (!this.subscribers[event]) return;

    const subscribers = [...this.subscribers[event]];
    
    subscribers.forEach(subscriber => {
      try {
        subscriber.callback(data, event);
        
        // Remove one-time subscribers
        if (subscriber.once) {
          this.unsubscribe(event, subscriber.id);
        }
      } catch (error) {
        console.error(`Error in subscriber for event "${event}":`, error);
      }
    });

    // Also emit as a custom DOM event for broader compatibility
    if (options.emitDomEvent !== false) {
      const customEvent = new CustomEvent(event, {
        detail: data,
        bubbles: options.bubbles !== false,
        cancelable: options.cancelable !== false
      });
      
      document.dispatchEvent(customEvent);
    }
  }

  /**
   * Get all subscribers for an event
   * @param {string} event - Event name
   * @returns {array} Array of subscribers
   */
  getSubscribers(event) {
    return this.subscribers[event] || [];
  }

  /**
   * Clear all subscribers for an event
   * @param {string} event - Event name
   */
  clearEvent(event) {
    delete this.subscribers[event];
  }

  /**
   * Clear all subscribers
   */
  clearAll() {
    this.subscribers = {};
  }

  /**
   * Get all events that have subscribers
   * @returns {array} Array of event names
   */
  getEvents() {
    return Object.keys(this.subscribers);
  }
}

// Create global instance
const pubsub = new PubSub();

// Export to global scope for theme compatibility
window.PubSub = pubsub;
window.pubsub = pubsub;

// Helper functions for common events
window.PubSubHelpers = {
  /**
   * Publish cart update event
   * @param {object} cart - Cart data
   */
  cartUpdate: (cart) => {
    pubsub.publish(PUB_SUB_EVENTS.cartUpdate, cart);
  },

  /**
   * Publish cart error event
   * @param {string|Error} error - Error message or object
   */
  cartError: (error) => {
    pubsub.publish(PUB_SUB_EVENTS.cartError, error);
  },

  /**
   * Publish variant change event
   * @param {object} variant - Variant data
   * @param {object} product - Product data
   */
  variantChange: (variant, product) => {
    pubsub.publish(PUB_SUB_EVENTS.variantChange, { variant, product });
  },

  /**
   * Publish section update event
   * @param {string} sectionId - Section ID
   * @param {HTMLElement} element - Updated element
   */
  sectionUpdate: (sectionId, element) => {
    pubsub.publish(PUB_SUB_EVENTS.sectionUpdate, { sectionId, element });
  }
};

// Initialize common event listeners
document.addEventListener('DOMContentLoaded', () => {
  // Listen for cart form submissions
  document.addEventListener('submit', (event) => {
    if (event.target.matches('form[action="/cart/add"]')) {
      pubsub.publish(PUB_SUB_EVENTS.productFormSubmit, {
        form: event.target,
        formData: new FormData(event.target)
      });
    }
  });

  // Listen for variant selector changes
  document.addEventListener('change', (event) => {
    if (event.target.matches('input[name="id"], select[name="id"]')) {
      const form = event.target.closest('form');
      const productData = form ? form.dataset : {};
      
      pubsub.publish(PUB_SUB_EVENTS.variantChange, {
        variantId: event.target.value,
        form: form,
        productData: productData
      });
    }
  });
});

// ES6 exports removed for browser compatibility
// PubSub is already available as window.PubSub and window.pubsub